import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontFamily: 'Helvetica', fontSize: 10 },
  header: { marginBottom: 20, borderBottom: '2 solid #C9A96E', paddingBottom: 10 },
  title: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: '#C9A96E' },
  subtitle: { fontSize: 10, color: '#666', marginTop: 4 },
  sectionTitle: { fontSize: 13, fontFamily: 'Helvetica-Bold', marginTop: 20, marginBottom: 8, color: '#333' },
  row: { flexDirection: 'row', marginBottom: 4 },
  label: { width: 200, color: '#666' },
  value: { fontFamily: 'Helvetica-Bold' },
  table: { marginTop: 8 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#f0f0f0', padding: 6, borderBottom: '1 solid #ddd' },
  tableRow: { flexDirection: 'row', padding: 6, borderBottom: '1 solid #eee' },
  tableCell: { flex: 1, fontSize: 8 },
  tableCellBold: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold' },
  footer: { position: 'absolute', bottom: 30, left: 40, right: 40, borderTop: '1 solid #eee', paddingTop: 8, fontSize: 8, color: '#999' },
  alertRow: { flexDirection: 'row', padding: 4, borderBottom: '1 solid #eee' },
  alertSeverity: { width: 60, fontSize: 8, fontFamily: 'Helvetica-Bold' },
  alertTitle: { flex: 1, fontSize: 8 },
  alertDate: { width: 80, fontSize: 8, color: '#666' },
});

interface MonthlySummaryProps {
  monthLabel: string;
  received: { count: number; weight: number };
  exported: { count: number; weight: number };
  pipeline: { count: number; weight: number };
  workers: Array<{ name: string; piecesCompleted: number; avgYield: number; gradeAPct: number }>;
  workshopAvgYield: number;
  activePackages: Array<{ invoiceNumber: string; itemNumber: string; supplier: string; totalStones: number; completionPct: number }>;
  alerts: Array<{ severity: string; title: string; createdAt: string }>;
}

export function MonthlySummaryPDF(props: MonthlySummaryProps) {
  const { monthLabel, received, exported, pipeline, workers, workshopAvgYield, activePackages, alerts } = props;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Aditi Diamonds | Monthly Operations Report</Text>
          <Text style={styles.subtitle}>{monthLabel}</Text>
        </View>

        {/* Section 1: Overview */}
        <Text style={styles.sectionTitle}>1. Overview</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Stones received this month:</Text>
          <Text style={styles.value}>{received.count} stones ({received.weight} ct)</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Stones exported this month:</Text>
          <Text style={styles.value}>{exported.count} stones ({exported.weight} ct)</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Currently in pipeline:</Text>
          <Text style={styles.value}>{pipeline.count} stones ({pipeline.weight} ct)</Text>
        </View>

        {/* Section 2: Worker Performance */}
        <Text style={styles.sectionTitle}>2. Worker Performance</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Workshop average yield:</Text>
          <Text style={styles.value}>{workshopAvgYield}%</Text>
        </View>
        {workers.length > 0 && (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCellBold}>Worker</Text>
              <Text style={styles.tableCellBold}>Pieces Completed</Text>
              <Text style={styles.tableCellBold}>Avg Yield</Text>
              <Text style={styles.tableCellBold}>Grade A%</Text>
            </View>
            {workers.map((w, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={styles.tableCell}>{w.name}</Text>
                <Text style={styles.tableCell}>{w.piecesCompleted}</Text>
                <Text style={styles.tableCell}>{w.avgYield}%</Text>
                <Text style={styles.tableCell}>{w.gradeAPct}%</Text>
              </View>
            ))}
          </View>
        )}

        {/* Section 3: Package Status */}
        <Text style={styles.sectionTitle}>3. Active Packages</Text>
        {activePackages.length > 0 ? (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCellBold}>Invoice / Item</Text>
              <Text style={styles.tableCellBold}>Supplier</Text>
              <Text style={styles.tableCellBold}>Stones</Text>
              <Text style={styles.tableCellBold}>Completion</Text>
            </View>
            {activePackages.map((p, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={styles.tableCell}>{p.invoiceNumber}/{p.itemNumber}</Text>
                <Text style={styles.tableCell}>{p.supplier}</Text>
                <Text style={styles.tableCell}>{p.totalStones}</Text>
                <Text style={styles.tableCell}>{p.completionPct}%</Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={{ color: '#999', marginTop: 4 }}>No active packages</Text>
        )}

        {/* Section 4: Alerts */}
        <Text style={styles.sectionTitle}>4. Critical / High Alerts</Text>
        {alerts.length > 0 ? (
          <View style={styles.table}>
            {alerts.map((a, i) => (
              <View key={i} style={styles.alertRow}>
                <Text style={[styles.alertSeverity, { color: a.severity === 'CRITICAL' ? '#e53935' : '#f57c00' }]}>
                  {a.severity}
                </Text>
                <Text style={styles.alertTitle}>{a.title}</Text>
                <Text style={styles.alertDate}>{new Date(a.createdAt).toLocaleDateString()}</Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={{ color: '#999', marginTop: 4 }}>No critical or high alerts this month</Text>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Generated by Aditi Diamonds Vault | {new Date().toLocaleDateString()}</Text>
        </View>
      </Page>
    </Document>
  );
}
